/* GASSISA
=========================================================================================
* Descripción: Almacén aditivo de perfiles multi-tenant (multi-licencia) por equipo.
*              NO modifica sessionStorage ni arsync-tenant-device (perfil activo legacy).
*              PrinterTools conserva UNA sola licencia; los perfiles secundarios viven
*              solo aquí y comparten fingerprint/machineName del hardware.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*      1       | 08/10/2026 | LIC. José Luis Aragón C. | Creación multi-perfil aditivo
* ---------------------------------------------------------------------------------------
*/

export const tenantProfilesStorageName = "arsync-tenant-profiles";

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
};

const normKey = (value) => String(value ?? "").trim().toUpperCase();

export const normalizeTenantProfile = (profile) => {
  if (!profile) return profile;
  const normalized = { ...profile };
  if (typeof normalized.tenantKey === "string") normalized.tenantKey = normKey(normalized.tenantKey);
  if (typeof normalized.licenseCode === "string") normalized.licenseCode = normKey(normalized.licenseCode);
  if (typeof normalized.devicePublicId === "string") normalized.devicePublicId = normKey(normalized.devicePublicId);
  if (typeof normalized.deviceKey === "string") normalized.deviceKey = normKey(normalized.deviceKey);
  if (typeof normalized.tenantName === "string") normalized.tenantName = String(normalized.tenantName ?? "").trim();
  if (typeof normalized.databaseName === "string") normalized.databaseName = String(normalized.databaseName ?? "").trim();
  if (typeof normalized.deviceAlias === "string") normalized.deviceAlias = String(normalized.deviceAlias ?? "").trim();
  if (typeof normalized.machineName === "string") normalized.machineName = String(normalized.machineName ?? "").trim();
  if (typeof normalized.fingerprint === "string") normalized.fingerprint = String(normalized.fingerprint ?? "").trim();
  return normalized;
};

export const listTenantProfiles = () => {
  try {
    const raw = safeGet(tenantProfilesStorageName);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeTenantProfile).filter((p) => p?.tenantKey && p?.devicePublicId && p?.deviceKey);
  } catch {
    return [];
  }
};

export const saveTenantProfile = (profile) => {
  const normalized = normalizeTenantProfile(profile);
  if (!normalized?.tenantKey || !normalized?.devicePublicId || !normalized?.deviceKey) return listTenantProfiles();
  const current = listTenantProfiles();
  const idx = current.findIndex((p) => normKey(p.tenantKey) === normKey(normalized.tenantKey));
  const next = [...current];
  const merged = {
    ...(idx >= 0 ? current[idx] : {}),
    ...normalized,
    lastUsedAt: normalized.lastUsedAt || new Date().toISOString(),
  };
  if (idx >= 0) next[idx] = merged;
  else next.push(merged);
  safeSet(tenantProfilesStorageName, JSON.stringify(next));
  return next;
};

export const removeTenantProfile = (tenantKey) => {
  const next = listTenantProfiles().filter((p) => normKey(p.tenantKey) !== normKey(tenantKey));
  safeSet(tenantProfilesStorageName, JSON.stringify(next));
  return next;
};

export const findTenantProfile = (tenantKey) =>
  listTenantProfiles().find((p) => normKey(p.tenantKey) === normKey(tenantKey)) ?? null;

export const matchesProfileLicense = (profile, licenseCode) => {
  const target = normKey(licenseCode);
  if (!profile || !target) return false;
  return [profile.licenseCode, profile.tenantKey].map(normKey).some((v) => v === target);
};
